import { useState, useRef, useEffect } from 'react';
import {
  put,
  patch,
  post,
  deleteReq,
  DELETE,
  PATCH,
  POST,
  PUT,
  type NETWORK_CONFIG,
  isTypeFormData,
  COMPONENT,
  type MUTATION_METHOD,
  recursiveTrim,
  interpolateString
} from '@dfg/brahma-utils';
import { AlertRegistry } from '../alerts/alerts';
import { HTTP_STATUS_CODE } from '@dfg/brahma-types';
import type { MutationUrlParams } from './constants';
import { AUTO_CLOSE_DURATION, SKIP_TASK_API_URL } from './constants';

const apiCall = async <T>(config: NETWORK_CONFIG) => {
  // eslint-disable-next-line no-useless-catch
  try {
    let result: T;
    switch (config.options.method) {
      case POST:
        result = await post(config.url, config.options);
        return result;

      case PUT:
        result = await put(config.url, config.options);
        return result;

      case DELETE:
        result = await deleteReq(config.url, config.options);
        return result;

      case PATCH:
        result = await patch(config.url, config.options);
        return result;
    }
  } catch (error) {
    throw error;
  }
};

const buildUrl = (baseUrl: string, urlParams?: MutationUrlParams): string => {
  let url = baseUrl;

  url = interpolateString(
    url,
    urlParams
      ? { ...(urlParams.queryParams ?? {}), ...(urlParams.pathParams ?? {}) }
      : {}
  );

  return url;
};

const useMutation = <T, S>(
  config: NETWORK_CONFIG,
  retry: number = 0,
  showToastOnError: boolean = true,
  shouldThrowError: boolean = false
) => {
  const [data, setData] = useState<null | T>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<any>(null);
  const retryLeft = useRef<number>(retry);

  const fetchData = async (
    data: S,
    urlParams?: MutationUrlParams
  ): Promise<T | null> => {
    try {
      setLoading(true);
      if (retryLeft.current > 0) {
        retryLeft.current = retryLeft.current - 1;
      }

      const effectiveUrl = buildUrl(config.url, urlParams);

      if (!config?.component && process.env.NODE_ENV === 'development') {
        console.error('Please add component name to this API', effectiveUrl);
      }

      const localConfig = {
        ...config,
        url: effectiveUrl,
        options: {
          ...config.options,
          headers: {
            ...config.options.headers,
            ...(config?.component ? { [COMPONENT]: config?.component } : {})
          },
          body: isTypeFormData(data)
            ? data
            : JSON.stringify(recursiveTrim(data))
        }
      };

      const result: T | null = await apiCall<T>(localConfig);

      setData(result);
      setLoading(false);
      setError(null);
      retryLeft.current = retry;

      return result;
    } catch (error: any) {
      if (retryLeft.current > 0) {
        return await fetchData(data, urlParams);
      } else {
        setData(null);
        setError(error);
        setLoading(false);
        retryLeft.current = retry;
        throw error;
      }
    }
  };

  useEffect(() => {
    const handleUnassignApiFailure = async (caseId: string) => {
      const maxRetries = 3;
      let retryCount = 0;

      while (retryCount < maxRetries) {
        try {
          const payload = {
            caseId
          };
          const retryConfig = {
            ...config,
            url: SKIP_TASK_API_URL,
            options: {
              ...config.options,
              method: PATCH as MUTATION_METHOD,
              headers: {
                ...config.options.headers,
                ...(config?.component ? { [COMPONENT]: config?.component } : {})
              },
              body: isTypeFormData(payload) ? payload : JSON.stringify(payload)
            }
          };
          await apiCall(retryConfig);
          break;
        } catch (retryError: any) {
          retryCount += 1;
          AlertRegistry.addToast({
            content: `Retry attempt ${retryCount} failed: ${retryError?.error?.dfgMessage}`,
            variant: 'error',
            autoCloseDuration: AUTO_CLOSE_DURATION,
            keepPreviousToast: false
          });

          if (retryCount >= maxRetries) {
            AlertRegistry.addToast({
              content: 'Max retry attempts reached',
              variant: 'error',
              autoCloseDuration: AUTO_CLOSE_DURATION,
              keepPreviousToast: false
            });
            break;
          }
        }
      }
    };
    const errorMessages = error?.error?.dfgMessage?.split('||');
    if (
      error &&
      AlertRegistry.showMultipleErrorModal &&
      errorMessages?.length > 1
    ) {
      AlertRegistry.showMultipleErrorModal(errorMessages);
    }

    if (
      error &&
      AlertRegistry.showUnauthorizedModal &&
      error?.error?.dfgErrorCode === 'GEN_403'
    ) {
      AlertRegistry.showUnauthorizedModal();
    } else if (error && showToastOnError) {
      if (
        error?.error?.dfgCode === String(HTTP_STATUS_CODE.UNASSIGN_API_FAILURE)
      ) {
        AlertRegistry.addToast({
          content:
            error?.error?.dfgMessage ?? 'Error while unassigning the task',
          variant: 'error',
          autoCloseDuration: AUTO_CLOSE_DURATION,
          keepPreviousToast: false
        });
        if (error?.error?.rootCauseError?.message) {
          void handleUnassignApiFailure(error?.error?.rootCauseError?.message);
        }
      } else {
        AlertRegistry.addToast({
          content: error?.error?.dfgMessage ?? 'Something went wrong',
          variant: 'error',
          autoCloseDuration: AUTO_CLOSE_DURATION,
          keepPreviousToast: false
        });
      }
    }
  }, [error]);

  const mutateAsync = async (
    data: S,
    urlParams?: MutationUrlParams
  ): Promise<T | null> => {
    setError(null);
    try {
      const response = await fetchData(data, urlParams);
      return response;
    } catch (error: any) {
      if (shouldThrowError) {
        throw error;
      }
      return null;
    }
  };

  const reset = () => {
    setData(null);
    setError(null);
    setLoading(false);
  };

  return { data, loading, error, mutateAsync, reset };
};

export default useMutation;
export type { MutationUrlParams };
